import React, { useState } from 'react';
import { FilePond, registerPlugin } from 'react-filepond';
import FilePondType, {
    FetchServerConfigFunction,
    LoadServerConfigFunction,
    ProcessServerConfigFunction,
    RemoveServerConfigFunction,
    RestoreServerConfigFunction,
    RevertServerConfigFunction,
    ServerUrl
} from 'filepond';
import FilePondPluginFileValidateSize from 'filepond-plugin-file-validate-size';
import FilePondPluginFileValidateType from 'filepond-plugin-file-validate-type';
import FilePondPluginImageExifOrientation from 'filepond-plugin-image-exif-orientation';
import FilePondPluginImagePreview from 'filepond-plugin-image-preview';
import 'filepond-plugin-image-preview/dist/filepond-plugin-image-preview.css';
import 'filepond/dist/filepond.min.css';

export interface FilePondServerType {
    url?: string;
    timeout?: number;
    headers?: { [key: string]: string | boolean | number };
    process?: string | ServerUrl | ProcessServerConfigFunction | null;
    revert?: string | ServerUrl | RevertServerConfigFunction | null;
    restore?: string | ServerUrl | RestoreServerConfigFunction | null;
    load?: string | ServerUrl | LoadServerConfigFunction | null;
    fetch?: string | ServerUrl | FetchServerConfigFunction | null;
    remove?: RemoveServerConfigFunction | null;
}

export type FileUploadProps = {
    /** The files that will be uploaded to the database */
    files?: Array<FilePondType.FilePondFile>;
    /** Array of accepted file types. Can be mime types or wild cards. For instance ['image/*'] will accept all images. ['image/png', 'image/jpeg'] will only accepts PNGs and JPEGs. */
    acceptedFileTypes?: Array<string>;
    /** Once the files have been set or processed this function is used to store them locally etc */
    onFilesChanged: (files: Array<FilePondType.FilePondFile>) => void;
    /** How big the file can be in kb */
    maxFileSize?: string | null;
    /** The name of the upload component */
    name: string;
    /** Initial label text for filepond control */
    labelIdle: string;
    labelFileProcessing: string;
    labelTapToCancel: string;
    labelTapToUndo: string;
    labelFileProcessingComplete: string;
    /** The address of the server to upload to */
    server?: string | null | FilePondServerType;
    /** Decides if multiple files are allowed to be uploaded at once using FilePond */
    allowMultiple?: boolean;
    /** Decides whether the image preview is visible or not */
    allowImagePreview?: boolean;
    /** If the upload errors, this function should return the error and file if it exists */
    onError: (
        error: FilePondType.FilePondErrorDescription,
        file?: FilePondType.FilePondFile
    ) => void;
};

registerPlugin(
    FilePondPluginFileValidateType,
    FilePondPluginFileValidateSize,
    FilePondPluginImagePreview,
    FilePondPluginImageExifOrientation
);

/** This component is used to upload one or more files to a database */
const FileUpload = ({
    files,
    acceptedFileTypes = ['image/*'],
    onFilesChanged,
    maxFileSize = '400000',
    name,
    labelIdle,
    labelFileProcessing,
    labelTapToCancel,
    labelTapToUndo,
    labelFileProcessingComplete,
    server,
    allowMultiple = false,
    allowImagePreview = false,
    onError
}: FileUploadProps): JSX.Element => {
    const [localFiles, setLocalFiles] = useState<
        Array<FilePondType.FilePondFile> | undefined
    >(files);

    const onProcessFile = (
        error: FilePondType.FilePondErrorDescription | null,
        file: FilePondType.FilePondFile
    ) => {
        if (error === null) {
            if (localFiles) {
                const newFiles: Array<FilePondType.FilePondFile> = [
                    ...localFiles,
                    file
                ];
                setLocalFiles(newFiles);
                onFilesChanged(newFiles);
            } else {
                setLocalFiles([file]);
            }
        }
    };

    const onRemoveFile = (
        error: FilePondType.FilePondErrorDescription | null,
        file: FilePondType.FilePondFile
    ) => {
        if (error === null) {
            const currentUploadedFiles = localFiles;

            if (currentUploadedFiles) {
                const updatedUploadedFiles = currentUploadedFiles.filter(
                    (inspectFile) => inspectFile.id !== file.id
                );

                setLocalFiles(updatedUploadedFiles);
                onFilesChanged(updatedUploadedFiles);
            }
        }
    };

    const defaultServer: FilePondServerType = {
        url: './api/Attachment/',
        revert: 'upload/delete',
        process: 'upload',
        restore: null,
        load: null,
        fetch: null
    };

    return (
        <FilePond
            id={name}
            name={name}
            allowMultiple={allowMultiple}
            server={server || defaultServer}
            onprocessfile={onProcessFile}
            onremovefile={onRemoveFile}
            maxFileSize={maxFileSize}
            acceptedFileTypes={acceptedFileTypes}
            allowImagePreview={allowImagePreview}
            onerror={onError}
            labelIdle={labelIdle}
            labelFileProcessing={labelFileProcessing}
            labelTapToCancel={labelTapToCancel}
            labelTapToUndo={labelTapToUndo}
            labelFileProcessingComplete={labelFileProcessingComplete}
        />
    );
};

export default FileUpload;
